import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { ApiError, apiDelete, apiFetch, apiPost } from "@/lib/api";
import { budgetKeys, buyLogKeys } from "@/lib/query";

export type BudgetAwardStatus = "not_awarded" | "partial" | "awarded";

/**
 * Buy Log line item — the in-app shape returned by GET /api/projects/:id/budget.
 * Mirrors `budget_line_items` with joined cost code, awarded company, and
 * linked commitment for display. Also carries derived fields (`commitmentCount`,
 * `committedTotalDerived`, `awardStatus`) computed on the server from the
 * aggregate of linked commitments.
 */
export interface BudgetLineItem {
  id: string;
  budgetId: string;
  projectId: string;
  orgId: string;
  costCodeId: string;
  description: string;
  csiDivision: string | null;
  originalBudgetedAmt: string;
  awardValue: string;
  allowance: string;
  holdsAtBuy: string;
  committedAmount: string;
  pendingCommitmentAmt: string;
  billedToDate: string;
  variance: string | null;
  isOwnerContractLine: boolean;
  awardedProjectCompanyId: string | null;
  awardedCompanyId: string | null;
  linkedCommitmentId: string | null;
  isAllowance: boolean;
  scheduledValueText: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  costCode: {
    id: string;
    code: string;
    description: string | null;
    csiDivision: string | null;
    csiSubdivision: string | null;
  } | null;
  awardedCompany: {
    id: string;
    companyName: string;
    type: string | null;
  } | null;
  commitment: {
    id: string;
    commitmentNumber: string;
    commitmentType: string;
    status: string;
  } | null;
  commitmentCount: number;
  committedTotalDerived: string;
  awardStatus: BudgetAwardStatus;
}

export interface BudgetMeta {
  id: string;
  projectId: string;
  orgId: string;
  versionNumber: number;
  versionType: string;
  isCurrent: boolean;
  totalBudgetedCost: string;
  totalCommitted: string;
  totalBilledToDate: string;
  totalPaidToDate: string;
  budgetStatus: string;
  approvedAt: string | null;
  notes: string | null;
}

export interface BudgetAuditEntry {
  id: string;
  action: string;
  entityId: string;
  actorUserId: string | null;
  actorName: string | null;
  previousState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
  changedFields: string[] | null;
  createdAt: string;
}

export type ProjectBudgetResponse = (BudgetMeta & { lineItems: BudgetLineItem[] }) | null;

export interface CostCode {
  id: string;
  code: string;
  description: string | null;
  csiDivision: string | null;
  csiSubdivision: string | null;
  category: string | null;
  ownerPayApp: boolean;
}

export function useProjectBudget(projectId: string) {
  return useQuery({
    queryKey: budgetKeys.project(projectId),
    queryFn: () => apiFetch<ProjectBudgetResponse>(`/projects/${projectId}/budget`),
    enabled: !!projectId,
  });
}

export interface SaveBudgetLineItemPayload {
  id?: string;
  costCodeId: string;
  description: string;
  csiDivision?: string | null;
  originalBudgetedAmt: string;
  awardValue?: string;
  allowance?: string;
  holdsAtBuy?: string;
  awardedCompanyId?: string | null;
  isAllowance?: boolean;
  scheduledValueText?: string | null;
  sortOrder?: number;
}

export function useSaveProjectBudget(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { lineItems: SaveBudgetLineItemPayload[] }) =>
      apiPost<{ data: BudgetLineItem[] }>(`/projects/${projectId}/budget`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: buyLogKeys.project(projectId) });
      toast.success("Buy Log saved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useApproveProjectBudget(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ data: BudgetMeta }>(`/projects/${projectId}/budget/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      toast.success("Budget approved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export interface BlockingCommitment {
  id: string;
  commitmentNumber: string;
  title: string;
}

export function extractBlockingCommitments(err: unknown): BlockingCommitment[] | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409) return null;
  const body = err.body as { blockingCommitments?: BlockingCommitment[] } | null;
  return body?.blockingCommitments ?? null;
}

export function useDeleteBudgetLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineItemId: string) =>
      apiDelete(`/projects/${projectId}/budget/line-items/${lineItemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: buyLogKeys.project(projectId) });
      toast.success("Line item deleted");
    },
    onError: (err: Error) => {
      // 409 Conflict carries blockingCommitments — the DeleteBudgetLineDialog
      // renders that data inline, so suppress the generic toast in that case.
      if (err instanceof ApiError && err.status === 409) return;
      toast.error(err.message);
    },
  });
}

export function useBulkAddToBuyLog(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { lineItems: SaveBudgetLineItemPayload[] }) =>
      apiPost<{ data: BudgetLineItem[] }>(`/projects/${projectId}/budget`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: buyLogKeys.project(projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useReorderBudgetLineItems(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { orderedIds: string[] }) =>
      apiPost<{ ok: boolean }>(`/projects/${projectId}/budget/reorder`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetKeys.project(projectId) });
      toast.success("Line items reordered");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useProjectCostCodes(projectId: string) {
  return useQuery({
    queryKey: budgetKeys.costCodes(projectId),
    queryFn: () => apiFetch<CostCode[]>(`/projects/${projectId}/cost-codes`),
    enabled: !!projectId,
  });
}

export function useBudgetAudit(projectId: string, lineItemId?: string) {
  const qs = lineItemId ? `?lineItemId=${encodeURIComponent(lineItemId)}` : "";
  return useQuery({
    queryKey: [...budgetKeys.project(projectId), "audit", lineItemId ?? "all"] as const,
    queryFn: () => apiFetch<BudgetAuditEntry[]>(`/projects/${projectId}/budget/audit${qs}`),
    enabled: !!projectId,
  });
}

// ── Buy Log summary metrics ─────────────────────────────────────────────

export interface BuyLogSummary {
  totalBudget: number;
  totalAwarded: number;
  totalCommitted: number;
  totalVariance: number;
  percentBoughtOut: number; // ratio 0-1
  lineCount: number;
  awardedCount: number;
  committedCount: number;
}

/**
 * Convert a `numeric(15,2)` string to an integer number of cents. Returns 0
 * for null/undefined/non-numeric inputs (including text-value scheduled
 * values like "Incl. Above" that carry no numeric amount).
 */
function toCents(value: string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Pure reducer — exported for unit testing. Operates on the BudgetLineItem
 * shape returned by the API (numeric columns are serialized as strings).
 * Uses integer-cent math so grand totals reconcile exactly to the penny
 * (AC#7) even when a budget contains thousands of awkwardly-sized lines.
 */
export function computeBuyLogSummary(lineItems: BudgetLineItem[]): BuyLogSummary {
  let totalBudgetCents = 0;
  let totalAwardedCents = 0;
  let totalCommittedCents = 0;
  let awardedCount = 0;
  let committedCount = 0;

  for (const item of lineItems) {
    // Text-value lines ("Incl. Above", "By Owner", "Excluded") preserve
    // intent but carry $0 for numeric totals — toCents handles the non-
    // numeric string case and returns 0.
    const hasTextScheduled = !!item.scheduledValueText;
    totalBudgetCents += hasTextScheduled ? 0 : toCents(item.originalBudgetedAmt);

    const awardCents =
      toCents(item.awardValue) + toCents(item.allowance) + toCents(item.holdsAtBuy);
    totalAwardedCents += awardCents;

    const derived = item.committedTotalDerived;
    totalCommittedCents += toCents(derived ?? item.committedAmount);

    if (item.awardStatus === "awarded") awardedCount += 1;
    if ((item.commitmentCount ?? 0) > 0) committedCount += 1;
  }

  const lineCount = lineItems.length;
  const percentBoughtOut = lineCount > 0 ? awardedCount / lineCount : 0;

  return {
    totalBudget: totalBudgetCents / 100,
    totalAwarded: totalAwardedCents / 100,
    totalCommitted: totalCommittedCents / 100,
    totalVariance: (totalBudgetCents - totalAwardedCents) / 100,
    percentBoughtOut,
    lineCount,
    awardedCount,
    committedCount,
  };
}

/**
 * Derived summary metrics for the Buy Log header. Recomputes whenever the
 * underlying budget query changes.
 */
export function useBuyLogSummary(projectId: string): BuyLogSummary {
  const { data } = useProjectBudget(projectId);
  return useMemo(() => computeBuyLogSummary(data?.lineItems ?? []), [data?.lineItems]);
}

// ── Schedule of Values roll-up ──────────────────────────────────────────

export interface SoVRow {
  costCodeId: string;
  code: string;
  description: string;
  csiDivision: string | null;
  scheduledValue: number;
  totalAward: number;
  variance: number;
  percentAwarded: number; // ratio 0-1
  lineCount: number;
}

export interface SoVDivisionGroup {
  divisionKey: string;
  divisionLabel: string;
  rows: SoVRow[];
  scheduledValue: number;
  totalAward: number;
  variance: number;
}

export interface SoVRollup {
  groups: SoVDivisionGroup[];
  totalScheduledValue: number;
  totalAward: number;
  totalVariance: number;
  percentAwarded: number;
}

/**
 * Aggregate Buy Log line items into a Schedule of Values view.
 *
 * Line items are collapsed by `costCodeId` (so a cost code that appears on
 * multiple Buy Log rows becomes a single SoV row with summed amounts). The
 * resulting rows are then grouped by CSI Division for display.
 *
 * Exported for unit testing — the component just consumes the output.
 */
export function computeSoVRollup(lineItems: BudgetLineItem[]): SoVRollup {
  // Aggregate per cost code using integer cents so the final totals hit
  // AC#7's "reconcile to the penny" bar. Text-value scheduled lines
  // contribute 0 to the numeric total but still count toward lineCount.
  const byCode = new Map<string, SoVRow & { scheduledCents: number; totalAwardCents: number }>();
  for (const item of lineItems) {
    const svCents = item.scheduledValueText ? 0 : toCents(item.originalBudgetedAmt);
    const awardCents =
      toCents(item.awardValue) + toCents(item.allowance) + toCents(item.holdsAtBuy);
    const existing = byCode.get(item.costCodeId);
    if (existing) {
      existing.scheduledCents += svCents;
      existing.totalAwardCents += awardCents;
      existing.lineCount += 1;
    } else {
      byCode.set(item.costCodeId, {
        costCodeId: item.costCodeId,
        code: item.costCode?.code ?? "—",
        description: item.costCode?.description ?? item.description ?? "",
        csiDivision: item.costCode?.csiDivision ?? item.csiDivision ?? null,
        scheduledValue: 0,
        totalAward: 0,
        variance: 0,
        percentAwarded: 0,
        lineCount: 1,
        scheduledCents: svCents,
        totalAwardCents: awardCents,
      });
    }
  }

  for (const row of byCode.values()) {
    row.scheduledValue = row.scheduledCents / 100;
    row.totalAward = row.totalAwardCents / 100;
  }

  // Finalize per-row variance + %, then group by division using cents.
  const groupMap = new Map<
    string,
    SoVDivisionGroup & { scheduledCents: number; totalAwardCents: number }
  >();
  for (const row of byCode.values()) {
    row.variance = (row.scheduledCents - row.totalAwardCents) / 100;
    row.percentAwarded = row.scheduledCents > 0 ? row.totalAwardCents / row.scheduledCents : 0;

    const key = row.csiDivision ?? "—";
    const group = groupMap.get(key);
    if (group) {
      group.rows.push(row);
      group.scheduledCents += row.scheduledCents;
      group.totalAwardCents += row.totalAwardCents;
    } else {
      groupMap.set(key, {
        divisionKey: key,
        divisionLabel: key === "—" ? "Uncategorized" : `Division ${key}`,
        rows: [row],
        scheduledValue: 0,
        totalAward: 0,
        variance: 0,
        scheduledCents: row.scheduledCents,
        totalAwardCents: row.totalAwardCents,
      });
    }
  }

  const groups: SoVDivisionGroup[] = Array.from(groupMap.values())
    .map((g) => {
      const scheduledValue = g.scheduledCents / 100;
      const totalAward = g.totalAwardCents / 100;
      return {
        divisionKey: g.divisionKey,
        divisionLabel: g.divisionLabel,
        rows: [...g.rows].sort((a, b) => a.code.localeCompare(b.code)),
        scheduledValue,
        totalAward,
        variance: (g.scheduledCents - g.totalAwardCents) / 100,
      };
    })
    .sort((a, b) => a.divisionKey.localeCompare(b.divisionKey));

  let totalScheduledCents = 0;
  let totalAwardCents = 0;
  for (const g of groupMap.values()) {
    totalScheduledCents += g.scheduledCents;
    totalAwardCents += g.totalAwardCents;
  }

  return {
    groups,
    totalScheduledValue: totalScheduledCents / 100,
    totalAward: totalAwardCents / 100,
    totalVariance: (totalScheduledCents - totalAwardCents) / 100,
    percentAwarded: totalScheduledCents > 0 ? totalAwardCents / totalScheduledCents : 0,
  };
}
